/**
 * Framework-free announcement-banner logic shared by the AnnouncementBanner
 * component, the app shell, and app servers (for SSR dismissal).
 *
 * No React / DOM imports here so it can run server-side.
 */

export const DISMISS_COOKIE_KEY = 'announcement-banner-dismissed'

const VALID_THEMES = ['info', 'success', 'caution', 'error'] as const

export type AnnouncementTheme = (typeof VALID_THEMES)[number]

export interface AnnouncementCta {
  text: string
  url: string
}

export interface Announcement {
  enabled: boolean
  message: string
  cta?: AnnouncementCta
  theme?: AnnouncementTheme
  dismissible: boolean
}

const isTheme = (value: unknown): value is AnnouncementTheme =>
  typeof value === 'string' &&
  (VALID_THEMES as readonly string[]).includes(value)

/**
 * Parse the raw LaunchDarkly flag value for `fe.resi.announcement-banner`.
 * The flag uses JSON variants; older configs may hand us a plain object.
 * Returns null when disabled, empty, or unparseable.
 */
export const parseAnnouncement = (rawFlag: unknown): Announcement | null => {
  if (!rawFlag) return null

  let parsed: unknown = rawFlag
  if (typeof rawFlag === 'string') {
    try {
      parsed = JSON.parse(rawFlag)
    } catch {
      return null
    }
  }

  if (typeof parsed !== 'object' || parsed === null) return null

  const record = parsed as Record<string, unknown>
  if (!record.enabled || typeof record.message !== 'string' || !record.message)
    return null

  const ctaRecord =
    typeof record.cta === 'object' && record.cta !== null
      ? (record.cta as Record<string, unknown>)
      : null

  const cta =
    ctaRecord &&
    typeof ctaRecord.text === 'string' &&
    ctaRecord.text &&
    typeof ctaRecord.url === 'string' &&
    ctaRecord.url
      ? { text: ctaRecord.text, url: ctaRecord.url }
      : undefined

  return {
    enabled: true,
    message: record.message,
    cta,
    theme: isTheme(record.theme) ? record.theme : undefined,
    // dismissible defaults to true
    dismissible: record.dismissible !== false,
  }
}

/**
 * Non-cryptographic hash of the announcement (message + cta + theme). Stored as
 * the dismissal value so any change to the announcement re-shows the banner.
 */
export const fingerprintAnnouncement = (announcement: Announcement): string => {
  const value = JSON.stringify({
    message: announcement.message,
    cta: announcement.cta ?? null,
    theme: announcement.theme ?? null,
  })
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i)
    hash |= 0 // force 32-bit int
  }
  return Math.abs(hash).toString(36)
}

/**
 * Server-safe check: given the raw flag value and the dismissal cookie value,
 * is the current announcement dismissed? Lets an app server (or the shell)
 * decide the banner's initial visibility during SSR to avoid a flash.
 */
export const isAnnouncementDismissed = (
  rawFlag: unknown,
  dismissedFingerprint: string | undefined | null
): boolean => {
  if (!dismissedFingerprint) return false
  const announcement = parseAnnouncement(rawFlag)
  if (!announcement || !announcement.dismissible) return false
  return fingerprintAnnouncement(announcement) === dismissedFingerprint
}
